/**
 * Pure validation for the add-deduction form in `payroll_tab.tsx`.
 * Extracted to keep the component under 150 lines per CLAUDE.md's component
 * testability rule, and to allow unit testing without React rendering.
 */
import type {
  DeductionType,
  AddDeductionInput,
} from '../../../data/use_employee_deductions.js';

export const DEDUCTION_TYPES: DeductionType[] = [
  'tax',
  'superannuation',
  'court_order',
  'other',
];

export interface DeductionFormState {
  deduction_type: DeductionType;
  /** Raw text input for the amount field — converted to minor units on submit. */
  amount_text: string;
  is_percentage: boolean;
  effective_from: string;
  effective_to: string;
  reference: string;
}

export function defaultDeductionFormState(): DeductionFormState {
  return {
    deduction_type: 'other',
    amount_text: '',
    is_percentage: false,
    effective_from: '',
    effective_to: '',
    reference: '',
  };
}

export interface DeductionFormErrors {
  amount?: string;
  effective_from?: string;
}

/** Returns the error object — empty means the form is valid. */
export function validateDeductionForm(
  state: DeductionFormState,
): DeductionFormErrors {
  const errors: DeductionFormErrors = {};
  const amountNum = parseFloat(state.amount_text);
  if (state.amount_text.trim() === '' || isNaN(amountNum) || amountNum <= 0) {
    errors.amount = 'amount_required';
  }
  if (state.effective_from.trim() === '') {
    errors.effective_from = 'effective_from_required';
  }
  return errors;
}

export function isDeductionFormValid(state: DeductionFormState): boolean {
  return Object.keys(validateDeductionForm(state)).length === 0;
}

/**
 * Converts the form state to the `AddDeductionInput` the hook expects.
 * Amounts are stored as integer minor units (docs/money-rules.md):
 * - Fixed: cents (multiply displayed amount by 100)
 * - Percentage: basis points (multiply percentage by 100)
 * Caller must have already validated the form.
 */
export function buildAddDeductionInput(
  state: DeductionFormState,
): AddDeductionInput {
  const amountNum = parseFloat(state.amount_text);
  // Fixed: cents (display × 100). Percentage: basis points (% × 100).
  // Both use the same scale; if a % toggle is added, validate input via a
  // percentage-aware field (not MoneyInput) to keep the semantics correct.
  const amount_cents_or_bps = Math.round(amountNum * 100);

  const input: AddDeductionInput = {
    deduction_type: state.deduction_type,
    amount_cents_or_bps,
    is_percentage: state.is_percentage,
    effective_from: state.effective_from,
  };
  if (state.effective_to.trim() !== '') {
    input.effective_to = state.effective_to;
  }
  if (state.reference.trim() !== '') {
    input.reference = state.reference;
  }
  return input;
}

/**
 * Formats the amount for display in the deductions table.
 * Returns a string like "1 500.00" (fixed) or "5.00%" (percentage).
 */
export function formatDeductionAmount(
  amount_cents_or_bps: number,
  is_percentage: boolean,
): string {
  if (is_percentage) {
    return `${(amount_cents_or_bps / 100).toFixed(2)}%`;
  }
  return (amount_cents_or_bps / 100).toFixed(2);
}
